import { useLayoutEffect, useRef } from "react";
import { emptyScanState, feedKey, type ScanState } from "../lib/scanBuffer.js";

/**
 * Listens for a USB/Bluetooth HID scanner.
 *
 * The scanner presents as a keyboard, so there is no API to ask. All the
 * judgement lives in scanBuffer.ts, which is pure and tested; this is
 * only the wiring.
 *
 * Deliberately no browser scanning API. `BarcodeDetector` is Chrome-only
 * and the spec rules it out; the camera path is a separate, explicit
 * fallback rather than something this hook silently falls back to.
 */
export function useScanner(
  onScan: (code: string) => void,
  enabled = true,
): void {
  // A ref so a re-render mid-scan cannot drop a half-read barcode.
  const state = useRef<ScanState>(emptyScanState);
  const handler = useRef(onScan);
  handler.current = onScan;

  // A failed camera is visibly idle in this commit; delaying registration
  // until a passive effect can lose the first handheld scan.
  useLayoutEffect(() => {
    if (!enabled) {
      state.current = emptyScanState;
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      // Never steal keystrokes from a field someone is filling in. When
      // the scan box itself has focus the scanner simply fills it and
      // its Enter submits the form, which reaches the same lookup.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      const step = feedKey(state.current, event.key, Date.now());
      state.current = step.state;

      if (step.code) {
        event.preventDefault();
        handler.current(step.code);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}
